import { ChevronRight } from 'lucide-react'
import type { SuiviDto, SuiviProjectGroupDto } from '../../../../lib/api/v2/planning'
import { cn } from '../../../../lib/utils'

interface SuiviPanelProps {
  data: SuiviDto | null
  loading?: boolean
  onAlertClick?: (group: SuiviProjectGroupDto, anchorStep: string) => void
}

/**
 * "Suivi / Attention" panel (SOLY-PLAN-001 MS-10). Each project is a bordered TICKET card with a
 * severity spine, a count badge (how many points stacked on this chantier), and one actionable row
 * per point (severity dot + title + resolving data + the step it anchors to). Points auto-resolve
 * via the alert engine, so a cleared card simply disappears — the empty state is the reward.
 * Refetched on-mutation only by the page (no polling).
 */
export function SuiviPanel({ data, loading, onAlertClick }: SuiviPanelProps) {
  if (loading) {
    return <div className="p-3 text-sm text-muted-foreground" data-testid="planning-suivi-loading">Chargement…</div>
  }

  if (!data || data.pointCount === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-center" data-testid="planning-suivi-empty">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-xl text-emerald-600">✓</span>
        <p className="mt-3 text-sm font-medium text-emerald-700">Tout est sous contrôle</p>
        <p className="mt-1 text-xs text-muted-foreground">Aucun point d'attention ouvert</p>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col" data-testid="planning-suivi-panel">
      <div className="flex items-baseline justify-between border-b px-3 py-2.5">
        <h2 className="text-sm font-semibold">À suivre <span className="font-normal text-muted-foreground">({data.projectCount})</span></h2>
        <span className="text-xs text-muted-foreground">{data.pointCount} point{data.pointCount > 1 ? 's' : ''} d'attention</span>
      </div>

      <div className="flex-1 space-y-3 overflow-auto p-2.5">
        {data.projects.map((group) => {
          const hasHard = group.alerts.some((a) => a.severity === 'Hard')
          return (
            <div
              key={group.projectId}
              data-testid={`planning-suivi-group-${group.projectId}`}
              className="relative overflow-hidden rounded-xl border bg-card shadow-sm"
            >
              {/* severity spine */}
              <span className={cn('absolute inset-y-0 left-0 w-1', hasHard ? 'bg-red-500' : 'bg-amber-500')} aria-hidden />

              {/* ticket header: client + stacked-count badge */}
              <div className="flex items-center gap-2 py-2.5 pl-4 pr-3">
                <span className="truncate text-sm font-semibold">{group.clientName}</span>
                {group.reference && <span className="font-mono text-[11px] text-muted-foreground">{group.reference}</span>}
                <span
                  className={cn(
                    'ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums text-white',
                    hasHard ? 'bg-red-500' : 'bg-amber-500',
                  )}
                  title={`${group.alerts.length} point${group.alerts.length > 1 ? 's' : ''} d'attention`}
                >
                  {group.alerts.length}
                </span>
              </div>

              {/* one ticket row per point */}
              <div>
                {group.alerts.map((a, i) => (
                  <button
                    // P1/P2/M4/M5 are emitted once PER SLOT: one project can carry the same code twice.
                    key={`${a.code}-${i}`}
                    type="button"
                    onClick={() => onAlertClick?.(group, a.anchorStep)}
                    data-testid={`planning-suivi-alert-${group.projectId}-${a.code}`}
                    className="flex w-full items-center gap-2.5 border-t py-2 pl-4 pr-2.5 text-left hover:bg-muted/60"
                  >
                    <span
                      className={cn('mt-1 h-2 w-2 flex-none rounded-full', a.severity === 'Hard'
                        ? 'bg-red-500 ring-4 ring-red-100' : 'bg-amber-500 ring-4 ring-amber-100')}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-medium leading-snug">{a.title}</span>
                      <span className="block truncate text-xs text-muted-foreground">{a.context}</span>
                    </span>
                    {/* the row opens the detail scrolled to the anchored section — a plain affordance,
                        not a pseudo-button (the step chip duplicated the title and read as clickable) */}
                    <ChevronRight className="h-3.5 w-3.5 flex-none text-muted-foreground/60" aria-hidden />
                  </button>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
